import { useState, useEffect, FunctionComponent, useRef, useLayoutEffect, ReactNode } from 'react'
import { copyStyles } from '../utils'
import ReactDOM from 'react-dom'
import { logger } from '../services/Logger'
import { StyleChangedEventEmitter } from '../StyleChangedEventEmitter'
import { useNonIdempotentEffect } from '@/hooks/useNonIdempotentEffect'
import { EnvironmentProvider } from '@ark-ui/react'
import { PortalContextProvider } from '@/UIComponents2/PortalContext'

const log = logger.createChildLogger({ name: 'PortalModal' })

/**
 * <https://codepen.io/davidgilbertson/pen/xPVMqp?editors=0110>
 */
interface PortalModalProps {
    frameName: string
    optionsString?: string
    backgroundColor?: string
    inheritStyles?: boolean
    watchStyleChanges?: boolean
    fitContent?: boolean
    alwaysOnTop?: boolean
    noTransition?: boolean
    overflow?: boolean
    children?: ReactNode
    onDocument?: (document: Document) => void
    onClose: () => void
}
export const PortalModal: FunctionComponent<PortalModalProps> = ({
    frameName,
    optionsString,
    backgroundColor,
    inheritStyles = true,
    watchStyleChanges = false,
    fitContent,
    alwaysOnTop,
    noTransition,
    overflow,
    children,
    onDocument,
    onClose,
}) => {
    const [externalWindow, setExternalWindow] = useState<Window | null>(null)
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)

    // `dimensions` will be set if `fitContent` is true.
    const dimensions = useRef<{ width: number; height: number } | null>(null)

    useNonIdempotentEffect(() => {
        const containerElement = document.createElement('div')
        if (!noTransition) {
            containerElement.style.opacity = '0'
            containerElement.style.transition = 'opacity 0.1s ease'
            containerElement.style.transitionDelay = '0.1s'
            setTimeout(() => {
                containerElement.style.opacity = '1'
            }, 10)
        }

        // Handle dimension setting if `fitContent` is true (and by extension, `dimensions` is set.).
        // It is guaranteed that useLayoutEffect() will be called before this useEffect().
        let options = []
        if (dimensions.current) {
            let allowance = 5
            let width = dimensions.current.width + allowance
            let height = dimensions.current.height + allowance
            options.push([`width=${width}`])
            options.push([`height=${height}`])
        }
        if (typeof alwaysOnTop === 'boolean') {
            options.push([`alwaysOnTop=${alwaysOnTop}`])
        }
        const optionsStringFinal = (optionsString ? optionsString + ',' : '') + options.join(',')
        // this is what causes the native window to open in the main process (electron.js).
        const externalWindow = window.open('', frameName, optionsStringFinal)

        // append the element to the external document before setting ref
        // so that React could detect event binding correctly
        // https://github.com/facebook/react/issues/12355
        if (externalWindow) {
            if (backgroundColor && externalWindow.document.body?.style) {
                externalWindow.document.body.style.backgroundColor = backgroundColor
            }
            externalWindow.document.body.appendChild(containerElement)
            //externalWindow.document.body.style['height'] = 'unset'
            externalWindow.document.documentElement.style.overflowY = (overflow ?? true) ? 'auto' : 'hidden'
        }
        setContainerRef(containerElement)
        setExternalWindow(externalWindow)

        // copy styles from StyleChangedEventEmitter
        if (watchStyleChanges) {
            StyleChangedEventEmitter.on('changed', (cssString: string) => {
                if (!externalWindow) {
                    return
                }

                // remove <style> tags in <head>
                const styleTags = externalWindow.document.head.querySelectorAll('style')
                for (let tag of styleTags) {
                    tag.remove()
                }

                // add the style to `externalWindow.document.head`.
                const styleElement = externalWindow.document.createElement('style')
                styleElement.setAttribute('type', 'text/css')
                styleElement.setAttribute('data-jss', '')
                styleElement.setAttribute('data-comment', 'added by StyleChangedEventEmitter on changed event.')
                styleElement.appendChild(externalWindow.document.createTextNode(cssString))
                externalWindow.document.head.appendChild(styleElement)
            })
        }

        const beforeunloadHandler = () => onClose()
        externalWindow?.addEventListener('beforeunload', beforeunloadHandler)

        return () => {
            try {
                externalWindow?.removeEventListener('beforeunload', beforeunloadHandler)
            } catch (e) {}
            try {
                externalWindow?.close()
            } catch (e) {}
            onClose()
        }
    }, [])

    useEffect(() => {
        if (externalWindow) {
            // TODO: maybe document load event instead of timeout.
            // NOTE: it has been determined that this timeout is critical to getting all
            // the styles properly copied.
            if (inheritStyles) {
                setTimeout(() => {
                    copyStyles(document, externalWindow.document)
                    if (onDocument) {
                        onDocument(externalWindow.document)
                    }
                }, 0)
            }
        }
    }, [externalWindow])

    // If fitContent is set, renders the `children` while containerRef is not set and gets
    // its dimensions and saves it into the `dimensions` ref.
    const targetRef = useRef(null)
    useLayoutEffect(() => {
        if (fitContent && targetRef.current) {
            // @ts-ignore
            const { offsetWidth, offsetHeight } = targetRef.current
            dimensions.current = { width: offsetWidth, height: offsetHeight }
        }
    }, [])

    if (!containerRef) {
        if (fitContent) {
            return (
                <div
                    id="target-ref"
                    ref={targetRef}
                    style={{ width: 'fit-content', height: 'fit-content', opacity: 0 }}
                >
                    {children}
                </div>
            )
        } else {
            return null
        }
    } else {
        if (!externalWindow) {
            return null
        }
        return ReactDOM.createPortal(
            <PortalContextProvider container={externalWindow.document.body}>
                <EnvironmentProvider value={externalWindow.document}>{children}</EnvironmentProvider>
            </PortalContextProvider>,
            containerRef
        )
    }
}
